import { useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  InlineGrid,
  InlineStack,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";

const FREQUENCY_OPTIONS = [
  { label: "Every page visit", value: "0" },
  { label: "Once every hour", value: "1" },
  { label: "Once every day", value: "24" },
  { label: "Once every 3 days", value: "72" },
  { label: "Once every 7 days", value: "168" },
  { label: "Once every 30 days", value: "720" },
];

export default function CodExitIntentSettings({ settings, setSettings, loading, translationEditor = null, contentLocale }) {
  const digits = settings.decimal_digits ?? 2;
  const update = (key, value) => setSettings((current) => ({ ...current, [key]: value }));
  const rules = settings.cod_discount_rules || {};
  const updateRule = (key, value) => update("cod_discount_rules", { ...rules, [key]: value });
  const [minimumAmountEnabled, setMinimumAmountEnabled] = useState(Number(rules.minimum_amount) > 0);
  const [minimumQuantityEnabled, setMinimumQuantityEnabled] = useState(Number(rules.minimum_quantity) > 0);
  const ruleSummary = rules.type === "free_shipping" ? "Free shipping" : rules.type === "fixed" ? `${rules.value || 0} off products` : `${rules.value || 0}% off products`;
  const previewSubtotal = Math.max(100, Number(rules.minimum_amount) || 0);
  const previewQuantity = Math.max(2, Number(rules.minimum_quantity) || 0);
  const previewDiscount = rules.type === "free_shipping" ? 5 : rules.type === "fixed" ? Math.min(previewSubtotal, Number(rules.value) || 0) : previewSubtotal * Math.min(100, Number(rules.value) || 0) / 100;
  const previewMessage = (key, amount, quantity) => String(rules[key] || "").replaceAll("{remaining_amount}", amount).replaceAll("{remaining_quantity}", quantity).replaceAll("{discount_code}", settings.exit_intent_discount_code || "YOUR-CODE");
  const updateCode = (value) => {
    setSettings((current) => ({
      ...current,
      exit_intent_discount_code: value,
      exit_intent_enabled: value.trim().length > 0,
    }));
  };

  return (
    <BlockStack gap="400">
      <Banner tone="info">
        Create a COD-only offer. Use the same code for exit intent and the discount field.
      </Banner>

      <InlineGrid columns={{ xs: 1, lg: 2 }} gap="400">
        {translationEditor || <BlockStack gap="400">
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">Exit-intent discount</Text>
                  <Text as="p" tone="subdued">Configure the offer customers can apply to their COD form.</Text>
                </BlockStack>
                <Badge tone={settings.exit_intent_enabled ? "success" : undefined}>
                  {settings.exit_intent_enabled ? "Enabled" : "Disabled"}
                </Badge>
              </InlineStack>
              <Checkbox label="Enable COD discount" checked={Boolean(settings.exit_intent_enabled)} onChange={(value) => update("exit_intent_enabled", value)} disabled={loading} />
              <TextField
                label="COD discount code"
                value={settings.exit_intent_discount_code || ""}
                onChange={updateCode}
                placeholder="e.g. STAY10"
                helpText="Available in your COD form only."
                autoComplete="off"
                disabled={loading}
              />
              <BlockStack gap="200"><Text as="p">Discount type</Text><ButtonGroup variant="segmented" fullWidth>{[['fixed', 'Fixed amount'], ['percentage', 'Percentage'], ['free_shipping', 'Free shipping']].map(([value, label]) => <Button key={value} pressed={rules.type === value} onClick={() => updateRule("type", value)} disabled={loading}>{label}</Button>)}</ButtonGroup></BlockStack>
              {rules.type !== "free_shipping" ? <TextField label="Discount value" type="number" min={0.01} max={rules.type === "percentage" ? 100 : 99999999} suffix={rules.type === "percentage" ? "%" : undefined} value={String(rules.value ?? "10")} onChange={(value) => updateRule("value", value)} autoComplete="off" disabled={loading} /> : null}
              <Text as="p" tone="subdued">One code per order. Product discounts exclude shipping.</Text>
              <Text as="h3" variant="headingSm">Minimum requirements</Text>
              <Checkbox label="Minimum product subtotal" checked={minimumAmountEnabled || Number(rules.minimum_amount) > 0} onChange={(value) => { setMinimumAmountEnabled(value); updateRule("minimum_amount", value ? "50" : "0"); }} disabled={loading} />
              {minimumAmountEnabled || Number(rules.minimum_amount) > 0 ? <TextField label="Minimum amount (order currency)" type="number" min={0} value={String(rules.minimum_amount)} onChange={(value) => updateRule("minimum_amount", value)} autoComplete="off" disabled={loading} /> : null}
              <Checkbox label="Minimum quantity" checked={minimumQuantityEnabled || Number(rules.minimum_quantity) > 0} onChange={(value) => { setMinimumQuantityEnabled(value); updateRule("minimum_quantity", value ? 2 : 0); }} disabled={loading} />
              {minimumQuantityEnabled || Number(rules.minimum_quantity) > 0 ? <TextField label="Minimum item quantity" type="number" min={0} value={String(rules.minimum_quantity)} onChange={(value) => updateRule("minimum_quantity", value)} autoComplete="off" disabled={loading} /> : null}
              <Text as="p" tone="subdued">Both enabled requirements must be met. Shipping and tax are excluded.</Text>
              <Text as="h3" variant="headingSm">Eligibility messages</Text>
              {[['amount_message', 'Minimum amount not met'], ['quantity_message', 'Minimum quantity not met'], ['both_message', 'Both requirements not met']].map(([key, label]) => <TextField key={key} label={label} value={rules[key] || ""} onChange={(value) => updateRule(key, value)} multiline={2} maxLength={500} autoComplete="off" disabled={loading} />)}
              <Text as="p" tone="subdued">{'Available placeholders: {remaining_amount}, {remaining_quantity}, {discount_code}.'}</Text>
              <Box background="bg-surface-secondary" padding="300" borderRadius="200"><Text as="p">{ruleSummary} | Minimum {rules.minimum_amount || 0} and {rules.minimum_quantity || 0} items</Text></Box>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Detection triggers</Text>
              <Checkbox
                label="Desktop exit intent"
                helpText="Trigger when the pointer leaves from the top of the browser window."
                checked={Boolean(settings.exit_intent_desktop_enabled)}
                onChange={(value) => update("exit_intent_desktop_enabled", value)}
                disabled={loading}
              />
              <Checkbox
                label="Hesitation timer"
                helpText="Trigger when the customer stays on the COD form without ordering."
                checked={Boolean(settings.exit_intent_hesitation_enabled)}
                onChange={(value) => update("exit_intent_hesitation_enabled", value)}
                disabled={loading}
              />
              {settings.exit_intent_hesitation_enabled ? (
                <TextField
                  label="Hesitation delay (seconds)"
                  type="number"
                  min={5}
                  max={600}
                  value={String(settings.exit_intent_delay_seconds ?? 30)}
                  onChange={(value) => update("exit_intent_delay_seconds", value)}
                  autoComplete="off"
                  disabled={loading}
                />
              ) : null}
              <Checkbox
                label="Mobile inactivity"
                helpText="Trigger after no touch, scroll or input activity on smaller screens."
                checked={Boolean(settings.exit_intent_mobile_enabled)}
                onChange={(value) => update("exit_intent_mobile_enabled", value)}
                disabled={loading}
              />
              {settings.exit_intent_mobile_enabled ? (
                <TextField
                  label="Mobile inactivity (seconds)"
                  type="number"
                  min={5}
                  max={600}
                  value={String(settings.exit_intent_inactivity_seconds ?? 20)}
                  onChange={(value) => update("exit_intent_inactivity_seconds", value)}
                  autoComplete="off"
                  disabled={loading}
                />
              ) : null}
              <Select
                label="Display frequency"
                options={FREQUENCY_OPTIONS}
                value={String(settings.exit_intent_frequency_hours ?? 24)}
                onChange={(value) => update("exit_intent_frequency_hours", value)}
                disabled={loading}
              />
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Popup content</Text>
              <TextField label="Title" value={settings.exit_intent_title || ""} onChange={(value) => update("exit_intent_title", value)} maxLength={100} autoComplete="off" disabled={loading} />
              <TextField label="Description" value={settings.exit_intent_description || ""} onChange={(value) => update("exit_intent_description", value)} maxLength={255} multiline={3} autoComplete="off" disabled={loading} />
              <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                <TextField label="Continue button" value={settings.exit_intent_button_label || ""} onChange={(value) => update("exit_intent_button_label", value)} maxLength={60} autoComplete="off" disabled={loading} />
                <TextField label="Dismiss link" value={settings.exit_intent_dismiss_label || ""} onChange={(value) => update("exit_intent_dismiss_label", value)} maxLength={60} autoComplete="off" disabled={loading} />
              </InlineGrid>
            </BlockStack>
          </Card>
        </BlockStack>}

        <div lang={contentLocale} dir={contentLocale?.startsWith("ar") ? "rtl" : undefined}><BlockStack gap="400">
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">Customer preview</Text>
            <Box background="bg-surface-secondary" borderRadius="300" padding="800">
              <BlockStack gap="400" inlineAlign="center">
                <Text as="p" variant="headingLg" alignment="center">
                  {settings.exit_intent_title || "Wait — your discount is ready"}
                </Text>
                <Text as="p" tone="subdued" alignment="center">
                  {settings.exit_intent_description ?? "Complete your cash-on-delivery order now and save."}
                </Text>
                <Box background="bg-surface" borderRadius="200" padding="300" minWidth="160px">
                  <BlockStack gap="100" inlineAlign="center">
                    <Text as="p" tone="subdued">COD offer</Text>
                    <Text as="p" variant="headingMd">{settings.exit_intent_discount_code?.trim() || "YOUR-CODE"}</Text>
                  </BlockStack>
                </Box>
                <Box background="bg-fill-brand" borderRadius="200" paddingBlock="300" paddingInline="600">
                  <Text as="span" tone="text-inverse" fontWeight="semibold">
                    {settings.exit_intent_button_label || "Continue my order"}
                  </Text>
                </Box>
                <Text as="p" tone="subdued">{settings.exit_intent_dismiss_label || "No thanks"}</Text>
              </BlockStack>
            </Box>
            <Text as="p" tone="subdued">
              The exact discount and revised total appear after the COD form validates the code and its eligibility requirements.
            </Text>
          </BlockStack>
        </Card>
        <Card><BlockStack gap="300">
          <Text as="h2" variant="headingMd">COD form preview</Text>
          <Text as="p" tone="subdued">Example qualifying cart | Before tax</Text>
          <TextField label="Discount code" value={settings.exit_intent_discount_code || "YOUR-CODE"} autoComplete="off" readOnly connectedRight={<Button>Apply</Button>} />
          <Text as="p">Subtotal ({previewQuantity} items): {previewSubtotal.toFixed(digits)}</Text><Text as="p">Shipping: {(5).toFixed(digits)}</Text>
          <Text as="p" tone="success">Discount: -{previewDiscount.toFixed(digits)}</Text>
          <Text as="p" variant="headingLg">Total: {(previewSubtotal + 5 - previewDiscount).toFixed(digits)}</Text>
        </BlockStack></Card>
        <Card><BlockStack gap="300">
          <Text as="h2" variant="headingMd">Eligibility message preview</Text>
          {[['amount_message', 'Amount only'], ['quantity_message', 'Quantity only'], ['both_message', 'Amount and quantity']].map(([key, label]) => <BlockStack key={key} gap="200"><Text as="h3" variant="headingSm">{label}</Text><Banner tone="warning">{previewMessage(key, (20).toFixed(digits), "1")}</Banner></BlockStack>)}
          <Text as="p" tone="subdued">No discount is applied until all enabled requirements are met.</Text>
        </BlockStack></Card>
        </BlockStack></div>
      </InlineGrid>
    </BlockStack>
  );
}
